<template lang="pug">
.banner-and-interstitial-message
    .top
        .header
            h3.title {{ $t(`${BASE_TRANSLATION_PATH}.title`) }}
            aiq-tooltip(:content="$t(`${BASE_TRANSLATION_PATH}.tooltip`)"
                placement="top")
                fa-icon.question-mark(:icon="['fas', 'circle-question']")
        .add-section
            aiq-select.lang-select(v-model="selectedLanguage", placeholder="Select Language", size="small" :clearable="true" :suffix-icon="Icon.CaretBottom")
                aiq-option(v-for="item in availableLanguages", :key="item.code", :label="item.name", :value="item.code")
            i.el-icon-plus(@click="openEditor({})" class="add-icon add-celebration-btn")
    .content
        aiq-table(:data="alertMessages"
            class="celebration-setting-table")
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.message_type`)"
                label-class-name="celebration-tb-header"
                min-width="5")
                template(v-slot:default="scope")
                    span {{ scope.row.alert_type }}
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.message_frequency`)"
                label-class-name="celebration-tb-header"
                min-width="7")
                template(v-slot:default="scope")
                    span {{ scope.row.schedule_type }}
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.message_name_`)"
                label-class-name="celebration-tb-header"
                min-width="10")
                template(v-slot:default="scope")
                    span {{ scope.row.name }}
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.recurring_frequency`)"
                label-class-name="celebration-tb-header"
                min-width="8")
                template(v-slot:default="scope")
                    span {{ getRecurringFrequency(scope.row) }}
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.rank`)"
                label-class-name="celebration-tb-header"
                min-width="8")
                template(v-slot:default="scope")
                    span {{ scope.row.rank }}
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.start_date`, { time_zone: generalTzAbbr })"
                label-class-name="celebration-tb-header"
                min-width="10")
                template(v-slot:default="scope")
                    span {{ getDate(scope.row, 'effective_date') }}
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.end_date`, { time_zone: generalTzAbbr })"
                label-class-name="celebration-tb-header"
                min-width="10")
                template(v-slot:default="scope")
                    span {{ getDate(scope.row, 'expiration_date') }}
            aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.display_time`, { time_zone: generalTzAbbr })"
                label-class-name="celebration-tb-header"
                min-width="10")
                template(v-slot:default="scope")
                    span {{ getDisplayTime(scope.row) }}
            aiq-table-column(label-class-name="celebration-tb-header" min-width="5").edit-btn
                template(v-slot:default="scope")
                  .edit(@click="openEditor(scope.row)" class="edit-icon") {{ $t(`${BASE_TRANSLATION_PATH}.edit`) }}
EditBannerAndInterstitialMessage(
    v-if="isVisible"
    :selected-message="selectedMessage"
    @close-editor="closeEditor"
    :webchatLanguageStrings="webchatLanguageStrings"
    :lang_code="lang_code"
    :tzAbbr="tzAbbr"
    :timezone="timezone"
    @add-alert-key="addAlertKey"
    @update-schedule-type="updateScheduleType"
    @save-message="saveMessage"
    @update-message="updateMessage"
    @delete-message="deleteMsg"
    @update-every="updateEvery"
    @reset-message="resetMessage"
    @set_start_end_time="setTime"
    :segments="segments")
</template>
<script>
import get from 'lodash/get';
import dayjs from '@/localPlugins/dayjs';
import set from 'lodash/set';
import startCase from 'lodash/startCase';
import { mapGetters, mapState } from 'vuex';
import EditBannerAndInterstitialMessage from './edit.vue';
import { AVAILABLE_LANGUAGES } from '@/constants/settings';
import { useTimeZone, getTimeZoneAbbr, getMetricsTimeZoneAbbr } from '@/libs';

const SCHEDULE_TYPE_KEYS = {
  ONE_TIME: 'one-time',
  RECURRING: 'recurring',
};

export default {
  name: 'BannerAndInterstitialMessage',
  data() {
    return {
      BASE_TRANSLATION_PATH: 'settings_tab.channels_tab.webchat_configuration.banner_and_interstitial_messages',
      isVisible: false,
      selectedMessage: {},
      selectedLanguage: 'English',
      lang_code: 'en',
      availableLanguages: [],
      tzAbbr: '',
      timezone: '',
      generalTzAbbr: '',
    };
  },
  components: {
    EditBannerAndInterstitialMessage,
  },
  async mounted() {
    await this.getTzAbbr();
    const webchatLangSettings = await this.$store.dispatch('configs/getWebchatLanguageSettings');
    const enabled_langs = webchatLangSettings.enabled_languages || [];
    this.availableLanguages = AVAILABLE_LANGUAGES.filter(lang => enabled_langs.includes(lang.code));
    Promise.all([
      this.$store.dispatch('alertMessages/getAlertmessagesList'),
      this.$store.dispatch('configs/getWebchatLanguageStrings', { languagekey: this.lang_code }),
      this.$store.dispatch('configs/getCustomerSegments'),
    ]).then(() => {});

  },
  watch: {
    selectedLanguage(newValue) {
      this.lang_code = newValue || 'en';
      this.$store.dispatch('configs/getWebchatLanguageStrings', { languagekey: this.lang_code });
    },
  },
  computed: {
    ...mapState({
      createAlertMessagePending: state => state.alertMessages.createPending,
      updateAlertMessagePending: state => state.alertMessages.updatePending,
      alertMessages: state => state.alertMessages.alertMessages || [],
      webchatLanguageStrings: state => state.configs.language_strings,
    }),
    ...mapGetters({
      segments: 'configs/segmentNames',
    }),
  },
  methods: {
    getRecurringFrequency(item) {
      return get(item, 'recurring_frequency.frequency', '');
    },
    async getTzAbbr() {
      this.timezone = await useTimeZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        this.$store.dispatch,
        this.$store._state.data.configs,
      );

      this.generalTzAbbr = getMetricsTimeZoneAbbr(this.timezone);
      this.tzAbbr = getTimeZoneAbbr(this.timezone);
    },
    getDate(item, field) {
      const date = get(item, field, '');
      return dayjs(date).format('MM/DD/YYYY');
    },
    getDisplayTime(item) {
      const end = get(item, 'end_time');
      const start = get(item, 'start_time');
      const today = dayjs().format('YYYY-MM-DD');
      if (!start || !end) return '';
      const startTime = dayjs(`${today} ${start}`).tz(this.timezone).format('hh:mm A');
      const endTime = dayjs(`${today} ${end}`).tz(this.timezone).format('hh:mm A');
      return `${startTime} - ${endTime}`;
    },
    openEditor(message) {
      this.selectedMessage = message || {};
      if (!this.selectedMessage.id) {
        this.selectedMessage.schedule_type = SCHEDULE_TYPE_KEYS.ONE_TIME;
        this.selectedMessage.rank = 1;
        this.selectedMessage.recurring_frequency = {
          every: 1,
          frequency: 'daily',
        };
      }
      this.isVisible = true;
    },
    closeEditor() {
      this.isVisible = false;
    },
    addAlertKey(data) {
      set(this.selectedMessage, 'payload.message_langkey', data.key);
    },
    setTime({ start, end }) {
      set(this.selectedMessage, 'start_time', start);
      set(this.selectedMessage, 'end_time', end);
    },
    updateScheduleType(data) {
      this.selectedMessage.schedule_type = data.schedule_type;
    },
    updateEvery(data) {
      this.selectedMessage.recurring_frequency.every = data.every;
    },
    resetMessage(data) {
      if (!data.message.id) return;
      this.selectedMessage = data;
      //update alert message array where
      //selectedmessage id is equal to an object id
      const index = this.alertMessages.findIndex(x => x.id === data.message.id);
      this.alertMessages[index] = data.message;
      this.webchatLanguageStrings.webchat.banner_and_interstitial_messages[data.message.payload.message_langkey] = data.originalMsg;
    },
    updateMessage(data) {
      this.webchatLanguageStrings.webchat.banner_and_interstitial_messages[data.key] = data.message;
    },
    async deleteConfirm() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.title`, {
            name: this.selectedMessage.name}),
          this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.message`, {
            name: this.selectedMessage.name,
          }), {
            cancelButtonText: this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.cancel_button`),
            confirmButtonText: this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.confirm_button`),
          },
        );
        return true;
      } catch {
        return false;
      }
    },
    async deleteMsg() {
      try {
        const response = await this.deleteConfirm();
        if (response) {
          await this.$store.dispatch('alertMessages/deleteAlertmessage', this.selectedMessage.id);
          await this.$store.dispatch('alertMessages/getAlertmessagesList');
          this.$aiq.notify.success(`Chat ${startCase(this.selectedMessage.alert_type)} Message has been deleted.`);
        }
      } catch (ex) {
        this.$aiq.notify.error(`Unable to delete Chat ${startCase(this.selectedMessage.alert_type)} Message.`);
      }
    },
    async saveMessage(data) {
      try {
        if (!this.selectedMessage.segments) {
          this.selectedMessage.segments = [];
        }
        const interval = data.intervals || [];
        const frequency = data.frequency;

        this.selectedMessage.start_time = dayjs(data.start_time).tz(this.timezone, true);
        this.selectedMessage.end_time = dayjs(data.end_time).tz(this.timezone, true);

        if (this.selectedMessage.schedule_type === 'one-time') {
          this.selectedMessage.recurring_frequency = {};
          await this.$store.dispatch('alertMessages/createOrUpdate', {
            ...this.selectedMessage, date_interval: {
              intervals: interval,
            },
          });
        } else {
          await this.$store.dispatch('alertMessages/createOrUpdate', {
            ...this.selectedMessage, frequency, date_interval: {
              intervals: interval,
              recurring_options: data.recurring_options,
            },
          });
        }
        await this.$store.dispatch('configs/setWebchatLanguageStrings', {
          webchat: this.webchatLanguageStrings.webchat, languagekey: this.lang_code,
        });

        this.$aiq.notify.success(`${this.$t(`${this.BASE_TRANSLATION_PATH}.saved`)}`);
        this.isVisible = false;

        Promise.all([
          this.$store.dispatch('alertMessages/getAlertmessagesList'),
          this.$store.dispatch('configs/getWebchatLanguageStrings', { languagekey: this.lang_code }),
        ]).then(() => { });


      } catch (error) {
        this.$aiq.notify.error(error.response.data.error || 'Error saving message');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
    display: flex;
    float: left;
    justify-content: space-between;
}

.top {
  padding-bottom: 50px;
}

.md-theme-agentiq .content-inner .header .title {
    color: #324057;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
}
.question-mark {
    padding-left: 5px;
    padding-top:5px;
}
.add-section {
    float: right;
}
.edit-icon {
    cursor: pointer;
    color: #409eff;
}

.add-section {
    display: flex;
}
.add-celebration-btn {
    font-size: 14px;
    line-height: 14px;
    vertical-align: middle;
}

.add-icon {
    cursor: pointer;
    padding-top: 8px;
}

.lang-select {
  width: 200px;
  padding-right: 20px;
}

.celebration-setting-table {
    border: 1px solid #A8A9AB;
    border-radius: 2px;

    ::v-deep .celebration-tb-header {
      border-bottom: 1px solid #A8A9AB;
      .cell {
        padding: 8px;
        line-height: 18px;
        font-weight: 400;
        color: #28292C;
        background: #DDD;
      }
    }

    ::v-deep .el-table__body-wrapper {
      table.el-table__body {
        tbody {
          tr {
            td {
              padding: 5px 0px;

              .sel-occurrence {
                max-width: 200px;
              }
              .sel-rank {
                max-width: 100px;
              }
              .sel-visual {
                max-width: 200px;
              }
              .edit-btn {
                float: right;
              }
            }
          }
        }
      }
    }
  }
</style>